import { apiClient } from '@/utils/api/client';

export interface UserProgress {
  currentStep: number;
  eligibilityCompleted: boolean;
  documentsUploaded: boolean;
  identityVerified: boolean;
  bankingDetailsCompleted: boolean;
  lastCompletedStep: number;
  completedAt?: {
    eligibility?: string;
    authentication?: string;
    accountCreated?: string;
    emailConfirmed?: string;
    documentsUploaded?: string;
    bankingProvided?: string;
    contractStarted?: string;
    contractCompleted?: string;
    payoutScheduled?: string;
    payoutExecuted?: string;
  };
}

export interface ProgressResponse {
  success: boolean;
  progress: UserProgress;
  email?: string;
  message?: string;
  selectedIdentities?: string[];
  purchasePrice?: number;
}

export interface BankingDetails {
  iban: string;
  bankName?: string;
}

export interface User {
  _id: string;
  email: string;
  progress?: UserProgress;
}

export interface BankingInfo {
  ibanCensored: string | null;
  ibanVerified: boolean;
  hasBankingDetails: boolean;
}

export const userService = {
  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<{ success: boolean; user: User }>('/user/me');
    return response.user;
  },

  async getBankingDetails(): Promise<BankingInfo> {
    const response = await apiClient.get<{ success: boolean; banking: BankingInfo }>('/user/banking');
    return response.banking;
  },

  async getProgress(): Promise<ProgressResponse> {
    return apiClient.get<ProgressResponse>('/user/progress');
  },

  async updateProgress(updates: Partial<UserProgress>): Promise<ProgressResponse> {
    return apiClient.put<ProgressResponse>('/user/progress', updates);
  },

  async markStepCompleted(step: 'eligibility' | 'authentication' | 'documents' | 'identity' | 'banking'): Promise<ProgressResponse> {
    return apiClient.post<ProgressResponse>('/user/progress/step-completed', { step });
  },

  async updateBankingDetails(details: BankingDetails): Promise<{ success: boolean; message?: string }> {
    return apiClient.put<{ success: boolean; message?: string }>('/user/banking-details', details);
  },

  async updateSelectedIdentities(selectedIdentities: string[]): Promise<{ success: boolean; message?: string; purchasePrice: number; gmv: number }> {
    return apiClient.post<{ success: boolean; message?: string; purchasePrice: number; gmv: number }>('/user/selected-identities', { selectedIdentities });
  },

  async updateTracking(clarityId: string): Promise<{ success: boolean; message?: string }> {
    return apiClient.post<{ success: boolean; message?: string }>('/user/tracking', { clarityId });
  },
};